// Shared phone helpers for every guest/customer phone input (checkout, staff
// new-booking + edit, customer manage). Keeps the split/combine behaviour
// identical everywhere.

// Mobile country (dial) codes offered in the compact phone editors. NZ default.
export const COUNTRY_CODES = ["+64", "+61", "+1", "+44", "+86", "+81"];

// Split a stored phone ("+64 21 123 4567") into its dial code + the rest, so an
// editor can show a country selector + a number field. Falls back to +64.
export function splitPhone(raw: string | null | undefined): { country: string; number: string } {
  const v = (raw ?? "").trim();
  const match = COUNTRY_CODES.find((c) => v.startsWith(c));
  if (match) return { country: match, number: v.slice(match.length).trim() };
  return { country: "+64", number: v };
}

// Combine a dial code + local number into "+64 21 123 4567". Guards against the
// guest re-typing the country code in the number field (so it isn't duplicated),
// then drops the trunk zero. Handles all of: "+64 21…", "0064 21…", "64 21…",
// "021…", "21…" → "+64 21…". Returns "" when there's no number.
export function combinePhone(country: string, number: string): string {
  const cc = country.replace(/\D/g, "");          // dial digits, e.g. "64"
  let n = (number ?? "").trim()
    .replace(/^\++/, "")                           // leading "+"(s)
    .replace(/^00/, "");                           // international "00" prefix
  if (cc && n.replace(/\D/g, "").startsWith(cc)) {
    // Strip a duplicated leading country code (allowing a space/dash after it).
    n = n.replace(new RegExp(`^${cc}[\\s-]?`), "");
  }
  n = n.replace(/^[\s-]+/, "").replace(/^0+/, "").trim(); // separators + trunk zero
  return n ? `${country} ${n}` : "";
}

// Canonicalise a stored phone value at the write boundary. Keeps the
// "<dial> <number>" shape that checkout writes and splitPhone round-trips, while
// trimming and collapsing stray whitespace so the same number always stores the
// same way. Returns "" for an empty/blank value (clears the phone).
export function normalizePhone(raw: string | null | undefined): string {
  return (raw ?? "").trim().replace(/\s+/g, " ");
}
